import { mapGetters, mapState } from "vuex";

import comboComponent from "@/components/child/form/combo.vue";
import dateTimePickerComponent from "@/components/child/form/datetimepicker.vue";
import gridComponent from "@/components/child/grid/grid.vue";
import { GridColumn, GridColumnSearchColor, GridColumnType } from "@/components/child/grid/gridColumn";
import { Component, Prop, Watch } from "@/decorators";
import TipoBuscaVenda from "@/models/enum/tipoBuscaVenda";
import VendaModel, { VendaListParameters } from "@/models/vendaModel";
import preVendaService from "@/services/preVendaService";
import produtoService from "@/services/produto/produtoService";
import VendaService from "@/services/vendaService";
import vendedorService from "@/services/vendedorService";
import { AppState, Getters } from "@/store/store";
import { convertToDateInput, convertToDateInputString } from "@/utils/common/date";

import ListComponentBase from "../crud/listComponentBase";

import "./list.scss";

@Component({
    components: {
        gridComponent,
        dateTimePickerComponent,
        comboComponent,
    },
    computed: {
        ...mapState({
            paginationParams: (state: AppState) => state.paginationStatus.paginationParams,
        }),
        ...mapGetters(["GET_IS_ESTEREIS", "VALIDAR_PERMISSAO_USUARIO"] as Getters),
    },
})
export default class PreVendaListComponent extends ListComponentBase<VendaModel> {
    GET_IS_ESTEREIS: () => Promise<boolean>;
    VALIDAR_PERMISSAO_USUARIO: (telaDescricao: string, acaoController: string) => boolean;

    @Prop({ type: Boolean, default: false }) CalledByShortCut: boolean;
    @Prop({ type: Number, default: 20 }) pgSize: number;
    @Prop({ type: String, default: "codigo" }) sortKey: string;
    @Prop({ type: String, default: "desc" }) sortOrder: string;

    private vendaService = new VendaService();

    produtosOptions: Array<Object> = [];

    private model: VendaModel = null;

    isEstereis = false;

    usuariosOptions: Array<Object> = [];
    tipoBuscaVendaOptions: Array<Object> = [
        { text: "Codigo da Pré-Venda", value: TipoBuscaVenda.Venda },
        { text: "Cliente", value: TipoBuscaVenda.Cliente },
    ];

    isHidden = false;

    filtro: Array<Object> = [];
    radioFilter = 0;
    dataFilter1: Date = null;
    dataFilter2: Date = null;
    tipoBuscaVenda = 0;
    vendedor = null;
    produto = null;

    get gridColumns(): Array<GridColumnSearchColor> {
        const columns = [];
        columns.push(new GridColumnSearchColor("codigo", "Código", GridColumnType.String));
        columns.push(new GridColumnSearchColor("situacaoDescricao", "Situação", GridColumnType.String, false));
        columns.push(new GridColumnSearchColor("clienteNome", "Cliente", GridColumnType.String));
        columns.push(new GridColumnSearchColor("vendedorNome", "Vendedor", GridColumnType.String));
        columns.push(new GridColumnSearchColor("dataInclusao", "Data", GridColumnType.Date));
        columns.push(new GridColumnSearchColor("setorDescricao", "Setor", GridColumnType.String));
        columns.push(new GridColumnSearchColor("valorFinal", "Valor", GridColumnType.Money));

        return columns;
    }

    get gridColumnsVinculos(): Array<GridColumn> {
        return [
            new GridColumn("produtoDescricao", "Produto", GridColumnType.String),
            new GridColumn("continuo", "Contínuo", GridColumnType.Boolean),
            new GridColumn("quantidade", "quantidade", GridColumnType.DecimalPonto),
            new GridColumn("valorUnitario", "Valor Unitário", GridColumnType.Money),
            new GridColumn("valorTotal", "Valor Total", GridColumnType.Money),
            new GridColumn("dataValidade", "Data Validade", GridColumnType.Date),
        ];
    }

    public async afterMounted() {
        const response = await Promise.all([
            vendedorService.comboCached(this.usuarioLogado.franquiaId),
            produtoService.comboCached(this.usuarioLogado.franquiaId),
        ]);
        this.usuariosOptions = response[0];
        this.produtosOptions = response[1];

        if (this.CalledByShortCut) {
            this.pageSize = this.pgSize;
            this.gridSortKey = this.sortKey;
            this.gridSortOrder = this.sortOrder;
        }

        this.isEstereis = await this.GET_IS_ESTEREIS();
    }

    public async loadMethod() {
        const customParameters: VendaListParameters = {
            dataInicial: convertToDateInputString(this.filtro[0]),
            dataFinal: convertToDateInputString(this.filtro[1], true),
            radio: this.radioFilter > -1 ? this.radioFilter.toString() : "",
            tipoBuscaVenda: this.tipoBuscaVenda,
            vendedor: this.vendedor,
            produto: this.produto,
        };

        this.pageIndex = this.gridFilterKey !== this.filterKey ? 1 : this.pageIndex;
        this.filterKey = this.gridFilterKey;

        this.isHidden = false;

        this.gridData["dataDe"] = this.filtro[0] ? this.filtro[0].toString() : " ";
        this.gridData["dataAte"] = this.filtro[1] ? this.filtro[1].toString() : " ";
        this.gridData["status"] = this.filtro[2] ? this.filtro[2].toString() : " ";

        return await preVendaService.listPreVendas(
            this.gridFilterKey,
            this.gridSortKey,
            this.gridSortOrder,
            this.pags ? 1 : this.pageIndex,
            this.pags ? 999999 : this.pageSize,
            customParameters,
        );
    }

    private getFormValues(listaFiltro) {
        this.filtro = [];

        this.filtro.push(listaFiltro.target.elements.dataFilter1.value);
        this.filtro.push(listaFiltro.target.elements.dataFilter2.value);
        this.filtro.push(listaFiltro.target.elements.optradio.value);

        this.load();
    }

    @Watch("tipoBuscaVenda")
    private onTipoBuscaVenda() {
        this.load();
    }

    private async onRemoverPreVenda(model: VendaModel) {
        const response = await preVendaService.checkHasFaturamentoById(model.id);

        if (response) {
            await this.$showWarning(
                this.$t("__.ts.atencao"),
                this.$t("__.Components.parent.venda.listPreVenda_vue_html.naoEPossivelRemoverComFaturamento"),
            );
            return;
        }

        this.$router.push("/venda-cancelamento/" + model.id);
    }

    private beforeMount() {
        this.setProps(new VendaService(), "pre-venda", "codigo", false, "desc");
    }

    public async afterLoadPagination() {
        if (this.paginationParams.filtros != null) {
            this.filtro = this.paginationParams.filtros;
            if (this.filtro.length >= 0) {
                if (this.filtro[0]) {
                    this.dataFilter1 = convertToDateInput(this.filtro[0].toString());
                }
                if (this.filtro.length >= 1) {
                    if (this.filtro[1]) {
                        this.dataFilter2 = convertToDateInput(this.filtro[1].toString());
                    }
                }

                if (this.filtro.length >= 2) {
                    this.radioFilter = <number>this.filtro[2];
                }
            }
        }
    }
}
